import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { applyAdminTheme, forceDark } from "@/lib/theme";

const isAdminPath = (pathname) =>
  pathname.startsWith("/admin") || pathname.startsWith("/crm");

/**
 * Centralized, route-aware theme application.
 *
 *  • Any /admin* route  -> apply the saved admin preference (light/dark/system).
 *  • Every other route  -> force Dark (public TeamBuilder is always dark and
 *    never reads a saved preference or the OS setting).
 *
 * Re-applies on every navigation so moving Admin <-> Public instantly restores
 * the correct theme without a page refresh.
 */
export const ThemeManager = () => {
  const location = useLocation();

  useEffect(() => {
    if (isAdminPath(location.pathname)) applyAdminTheme();
    else forceDark();
  }, [location.pathname]);

  useEffect(() => {
    const reapply = () => {
      if (isAdminPath(window.location.pathname)) applyAdminTheme();
      else forceDark();
    };
    // Admin preference changed in-app (or in another tab).
    window.addEventListener("rd-theme-change", reapply);
    window.addEventListener("storage", reapply);
    // OS scheme change only matters when an admin route uses "System".
    const m = window.matchMedia ? window.matchMedia("(prefers-color-scheme: dark)") : null;
    if (m && m.addEventListener) m.addEventListener("change", reapply);
    else if (m && m.addListener) m.addListener(reapply);
    return () => {
      window.removeEventListener("rd-theme-change", reapply);
      window.removeEventListener("storage", reapply);
      if (m && m.removeEventListener) m.removeEventListener("change", reapply);
      else if (m && m.removeListener) m.removeListener(reapply);
    };
  }, []);

  return null;
};

export default ThemeManager;
